import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiHome, FiMapPin, FiUsers, FiDollarSign } from 'react-icons/fi';

interface Housing {
  building: string;
  roomNumber: string;
  roomType: string;
  moveInDate: string;
  moveOutDate: string;
  costPerSemester: number;
  roommates: Array<{
    name: string;
    email: string;
  }>;
  amenities: string[];
  address: string;
}

const MyHousingPage: React.FC = () => {
  const [housing, setHousing] = useState<Housing | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHousing = async () => {
      if (!currentUser) return;

      try {
        const docRef = doc(db, 'housing', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setHousing(docSnap.data() as Housing);
        }
      } catch (error) {
        console.error('Error fetching housing:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHousing();
  }, [currentUser]);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">My Housing</h1>
          <p className="text-gray-600 mt-2">View your housing assignment and details</p>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uc-navy"></div>
            <p className="mt-2 text-gray-600">Loading housing information...</p>
          </div>
        ) : !housing ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <FiHome size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No housing assignment found</p>
            <p className="text-sm text-gray-500 mt-2">Contact Housing Services for assistance</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Main Housing Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-uc-navy">{housing.building}</h2>
                  <p className="text-gray-600 mt-1">Room {housing.roomNumber}</p>
                </div>
                <div className="bg-uc-navy text-white px-4 py-2 rounded">
                  <p className="text-sm">Room Type</p>
                  <p className="font-semibold">{housing.roomType}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start">
                  <FiMapPin size={20} className="text-uc-gold mr-3 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="font-medium text-gray-900">{housing.address}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <FiDollarSign size={20} className="text-uc-gold mr-3 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Cost per Semester</p>
                    <p className="font-medium text-gray-900">${housing.costPerSemester.toLocaleString()}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <FiHome size={20} className="text-uc-gold mr-3 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Move-In Date</p>
                    <p className="font-medium text-gray-900">
                      {new Date(housing.moveInDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <FiHome size={20} className="text-uc-gold mr-3 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Move-Out Date</p>
                    <p className="font-medium text-gray-900">
                      {new Date(housing.moveOutDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Roommates */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-uc-navy mb-4 flex items-center">
                <FiUsers size={20} className="mr-2" />
                Roommates
              </h3>
              {housing.roommates.length === 0 ? (
                <p className="text-gray-600">No roommates assigned</p>
              ) : (
                <div className="space-y-3">
                  {housing.roommates.map((roommate, index) => (
                    <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-uc-navy rounded-full flex items-center justify-center text-white font-bold mr-3">
                        {roommate.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{roommate.name}</p>
                        <p className="text-sm text-gray-600">{roommate.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Amenities */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-uc-navy mb-4">Amenities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {housing.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className="w-2 h-2 bg-uc-gold rounded-full mr-2"></div>
                    <span className="text-sm text-gray-700">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyHousingPage;
